import {
  createBrowserRouter,
  Outlet,
  RouteObject,
  RouterProvider,
} from "react-router"
import { QueryClient } from "@tanstack/react-query"
import { lazy, PropsWithChildren } from "react"
import { Spinner } from "@phosphor-icons/react"
import { Root } from "./root.tsx"
import { GlobalError, ManualErrorRoute } from "./global-error"
import { RootLayout } from "./layouts/root-layout"
import { AppLayout } from "./layouts/app-layout"
import { bootstrapLoader } from "@/feature/auth/api/loaders"
import { PublicLayout } from "./layouts/public-layout"
import { Wizard } from "./wizard"
import { PrivacyPolicyPage } from "./legal/privacy"
import { ProtectedRoute } from "@/feature/auth/ProtectedRoute.tsx"
import { Welcome } from "./auth/signup/welcome.tsx"
import {
  ResendEmailForPendingUser,
  ResendEmailNotAuthenticated,
} from "./auth/resend/resend.tsx"
import { ConfirmScreen } from "./auth/confirm/confirm.tsx"
import { SendResetMail } from "./auth/reset/send.tsx"
import { ResetPassword } from "./auth/reset/reset-password.tsx"
import { Login } from "./auth/login/login.tsx"
import { AuthRootPage } from "./app/root.tsx"
import { NotFound } from "./404.tsx"
import { useAuthStore } from "@/feature/auth/store/authStore.ts"
import { Logout } from "./auth/logout/route.tsx"
import { SkeletonPageLoading } from "@/components/shared/loading/page.tsx"
import { useEcommerceRoutes } from "./app/financial-model/ecommerce/route.tsx"
import { useReportRoutes } from "@/routes/app/report/routes.tsx"
import { useFundraisingAdvisoryRoutes } from "@/routes/app/fundraising-advisory/routes.tsx"
import { useFinancialModelSettingsRoute } from "@/routes/app/financial-model/settings/routes.tsx"
import { useSettingsRoute } from "@/routes/app/settings/routes.tsx"
import { importOrReload } from "@/lib/utils/dynamic-import"
import { financialModelLoader } from "@/feature/financial-model/api/loader.ts"
import { useBootstapAppConfigQuery } from "@/feature/auth/api/index.ts"

type RouterProps = {
  queryClient: QueryClient
}

const LazyFinancialModel = lazy(() =>
  importOrReload(() =>
    import(`./app/financial-model/route.tsx`).then((mod) => ({
      default: mod.FinancialModel,
    }))
  )
)

const WorkSpaceRoute = lazy(() =>
  importOrReload(() =>
    import(`./app/workspace/route.tsx`).then((mod) => ({
      default: mod.WorkSpaceRoute,
    }))
  )
)

export const AppConfigWrapper = ({ children }: PropsWithChildren) => {
  const { isLoading } = useBootstapAppConfigQuery()
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner className="h-10 w-10 animate-spin" />
      </div>
    )
  }
  return children
}

export const AppRouter = (props: RouterProps) => {
  const { session } = useAuthStore()

  const publicRoutes = [
    {
      element: <RootLayout />,
      errorElement: <GlobalError />,

      children: [
        {
          path: `privacy-policy`,
          element: <PrivacyPolicyPage />,
        },
        ...(import.meta.env.DEV
          ? [
              {
                path: `/manual-error`,
                element: <ManualErrorRoute />,
              },
            ]
          : []),
      ],
    },
  ] as RouteObject[]
  const financialModelSettingsRoutes = useFinancialModelSettingsRoute(props)
  const settingsRoutes = useSettingsRoute(props)
  const ecommerceRoutes = useEcommerceRoutes()
  const reportRoutes = useReportRoutes(props)
  const fundraisingAdvisoryRoutes = useFundraisingAdvisoryRoutes(props)

  const authOnlyRoutes = [
    {
      element: <ProtectedRoute />,
      loader: bootstrapLoader(props.queryClient),
      hydrateFallbackElement: (
        <AppLayout withChildren>
          <SkeletonPageLoading />
        </AppLayout>
      ),
      errorElement: <GlobalError />,
      children: [
        {
          index: true,
          element: <AuthRootPage />,
        },
        {
          path: `logout`,
          element: <Logout />,
        },
        {
          path: `wizard`,
          element: <PublicLayout />,
          children: [
            {
              index: true,
              element: <Wizard />,
            },
          ],
        },
        {
          path: `resend`,
          element: <PublicLayout />,
          children: [
            {
              index: true,
              element: <ResendEmailForPendingUser />,
            },
          ],
        },
        {
          path: `dashboard`,
          element: <AppLayout />,
          children: [
            {
              index: true,
              element: <LazyFinancialModel />,
            },
            {
              path: `workspace`,
              element: <WorkSpaceRoute />,
            },
            {
              path: `financial-model/:id`,
              element: <Outlet />,
              loader: financialModelLoader(props.queryClient),
              children: [...financialModelSettingsRoutes, ...ecommerceRoutes],
            },
            ...settingsRoutes,
            ...reportRoutes,
            ...fundraisingAdvisoryRoutes,
          ],
        },
      ],
    },
  ]

  const routesForNotAuthenticatedOnly = [
    {
      element: <RootLayout />,
      children: [
        {
          index: true,
          element: <Root />,
          errorElement: <GlobalError />,
        },
      ],
    },
    {
      path: `/auth`,
      element: <PublicLayout />,
      children: [
        {
          path: `signup`,
          element: <Welcome />,
        },
        {
          path: `resend`,
          element: <ResendEmailNotAuthenticated />,
        },
        {
          path: `logout`,
          element: <Logout />,
        },
        {
          path: `confirm/:token`,
          element: <ConfirmScreen />,
          hydrateFallbackElement: <SkeletonPageLoading />,
        },
        {
          path: `login`,
          element: <Login />,
        },
        {
          path: `forgot-password`,
          children: [
            {
              index: true,
              element: <SendResetMail />,
            },
            {
              path: `:code`,
              element: <ResetPassword />,
            },
          ],
        },
      ],
    },
  ]

  const router = createBrowserRouter([
    ...publicRoutes,
    ...(!session?.access_token ? routesForNotAuthenticatedOnly : []),
    ...authOnlyRoutes,
    {
      path: `*`,
      element: <NotFound />,
    },
  ])

  return <RouterProvider router={router} />
}
